"use client";

import { useEffect, useCallback, useState, useRef } from 'react';
import { useAudioPlayerContext } from '@/contexts/audio-player-context';
import type { Sound } from '@/lib/supabase';

/**
 * Elements that already own Space as an activation or scroll key.
 *
 * Space activates a native `<button>`, toggles a checkbox/switch, and is left
 * to the browser (page-down) on a link. A document-level Space handler that
 * calls preventDefault() unconditionally takes that away — the user presses
 * Space on "Play X" and the control never fires (TES-1579 / WCAG 2.1.1).
 *
 * Matched with `closest()` rather than against the target alone, because a
 * keypress on a focused button reports the button, but a click-then-keypress
 * on nested markup can report an inner `<span>`. `[tabindex]:not([tabindex^="-"])`
 * is the catch-all for custom widgets that made themselves focusable.
 */
const SPACE_OWNING_SELECTOR = [
  'button',
  '[role="button"]',
  'a[href]',
  '[role="link"]',
  'input',
  'textarea',
  'select',
  'summary',
  'details',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="radio"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="option"]',
  '[contenteditable]',
  '[tabindex]:not([tabindex^="-"])',
].join(',');

function ownsSpaceKey(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.closest(SPACE_OWNING_SELECTOR) !== null;
}

interface UseKeyboardShortcutsOptions {
  sounds: Sound[];
  enabled?: boolean;
  onNextPage?: () => void;
  onPrevPage?: () => void;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
  onScrollToTop?: () => void;
  onSearchFocus?: () => void;
  onSaveCurrent?: () => void;
  onSoundFocus?: (index: number) => void;
}

export function useKeyboardShortcuts({
  sounds,
  enabled = true,
  onNextPage,
  onPrevPage,
  hasNextPage = false,
  hasPrevPage = false,
  onScrollToTop,
  onSearchFocus,
  onSaveCurrent,
  onSoundFocus,
}: UseKeyboardShortcutsOptions) {
  const player = useAudioPlayerContext();
  const [showHelp, setShowHelp] = useState(false);
  const [shortcutFlash, setShortcutFlash] = useState<string | null>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flashShortcut = useCallback((key: string) => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setShortcutFlash(key);
    flashTimerRef.current = setTimeout(() => setShortcutFlash(null), 800);
  }, []);

  useEffect(() => {
    const timerRef = flashTimerRef;
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    const target = event.target as HTMLElement;
    const isInInput = (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.isContentEditable
    );

    // Escape works everywhere — close help or blur input
    if (event.key === 'Escape') {
      if (showHelp) {
        setShowHelp(false);
        return;
      }
      if (isInInput) {
        (target as HTMLElement).blur();
        return;
      }
    }

    // Don't handle other shortcuts when in input fields
    if (isInInput) return;

    // / to focus search (works even with 0 sounds)
    if (event.key === '/' && onSearchFocus) {
      event.preventDefault();
      onSearchFocus();
      flashShortcut('/');
      return;
    }

    // ? toggles help (works even with 0 sounds)
    if (event.key === '?') {
      event.preventDefault();
      setShowHelp(prev => !prev);
      return;
    }

    if (sounds.length === 0) return;

    // 1-9: play/pause nth sound
    const keyNum = parseInt(event.key, 10);
    if (keyNum >= 1 && keyNum <= 9) {
      const index = keyNum - 1;
      const sound = sounds[index];
      if (sound) {
        event.preventDefault();
        if (player.currentSound?.id === sound.id && player.isPlaying) {
          player.pause();
        } else {
          player.play(sound);
        }
        flashShortcut(String(keyNum));
      }
      return;
    }

    // Space: transport control for the sound already loaded in the player.
    //
    // Two deliberate narrowings vs. the original handler (TES-1579):
    //
    //  1. Yield to any control that owns Space. Focus on a `<button>` — very
    //     often a "Play <sound>" button on a card — must activate *that*
    //     button. The old handler swallowed it and played an unrelated sound.
    //  2. Only claim Space once a sound is actually loaded. Pressing Space on
    //     a page the user has not interacted with used to start `sounds[0]`
    //     out of nowhere *and* suppress page-down scrolling. Space now falls
    //     through to the browser until the user has started something.
    //
    // preventDefault() is called only on the paths that actually act, so Space
    // never silently eats a scroll.
    if (event.key === ' ' || event.code === 'Space') {
      if (ownsSpaceKey(event.target)) return;
      if (!player.currentSound) return;

      event.preventDefault();
      if (player.isPlaying) {
        player.pause();
      } else {
        player.resume();
      }
      flashShortcut('space');
      return;
    }

    // s: save/unsound current sound
    if (event.key === 's' && onSaveCurrent && player.currentSound) {
      event.preventDefault();
      onSaveCurrent();
      flashShortcut('s');
      return;
    }

    // j: next sound in list (vim-style)
    if (event.key === 'j') {
      event.preventDefault();
      const currentIndex = sounds.findIndex(s => s.id === player.currentSound?.id);
      if (currentIndex < sounds.length - 1) {
        const nextIndex = currentIndex < 0 ? 0 : currentIndex + 1;
        player.play(sounds[nextIndex]);
        onSoundFocus?.(nextIndex);
        flashShortcut('j');
      }
      return;
    }

    // k: previous sound in list (vim-style)
    if (event.key === 'k') {
      event.preventDefault();
      const currentIndex = sounds.findIndex(s => s.id === player.currentSound?.id);
      if (currentIndex > 0) {
        player.play(sounds[currentIndex - 1]);
        onSoundFocus?.(currentIndex - 1);
        flashShortcut('k');
      } else if (currentIndex < 0 && sounds.length > 0) {
        player.play(sounds[0]);
        onSoundFocus?.(0);
        flashShortcut('k');
      }
      return;
    }

    // ArrowRight: next page
    if (event.key === 'ArrowRight') {
      if (hasNextPage && onNextPage) {
        event.preventDefault();
        onNextPage();
        onScrollToTop?.();
        flashShortcut('→');
      }
      return;
    }

    // ArrowLeft: previous page
    if (event.key === 'ArrowLeft') {
      if (hasPrevPage && onPrevPage) {
        event.preventDefault();
        onPrevPage();
        onScrollToTop?.();
        flashShortcut('←');
      }
      return;
    }
  }, [enabled, sounds, player, hasNextPage, hasPrevPage, onNextPage, onPrevPage, onScrollToTop, showHelp, onSearchFocus, onSaveCurrent, onSoundFocus, flashShortcut]);

  // Use a ref so the event-listener effect is stable (no re-registration on
  // every render) while handleKeyDown still always sees the latest closure.
  const handleKeyDownRef = useRef(handleKeyDown);
  useEffect(() => { handleKeyDownRef.current = handleKeyDown; });

  useEffect(() => {
    // eslint-disable-next-line react-doctor/advanced-event-handler-refs -- ref pattern already applied above
    const handler = (e: KeyboardEvent) => handleKeyDownRef.current(e);
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  return { showHelp, setShowHelp, shortcutFlash };
}